import { values } from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatGroupedData, getTotals, handleChartColors, handleChartHeight } from '../utils'
import { Activity, ActivityFormattedData, AgentActivityReportProps } from './types'
import { getActivityOveralls, getGroupData } from './utils'

const AgentActivityReportChart = (props: AgentActivityReportProps) => {
    const { t } = useTranslation()

    const overallLabel = t('app.common.overall')
    const totalLabel = t('app.common.total')

    const { reportsData } = props
    const reports = useMemo(() => values(reportsData) || [], [reportsData])
    const activityTypes = useMemo(() => reports[0]?.activities || [], [reports])

    const formattedData = formatGroupedData({
        data: reports,
        dataKey: 'agent',
        handleGroupData: (activity: Activity, agentName: string) => getGroupData(activity, agentName),
    }) as ActivityFormattedData[]

    const { activityOveralls } = useMemo(() => getActivityOveralls(reports, activityTypes), [reports, activityTypes])

    // adds activity overalls data ("Sveukupno") to formattedData array
    const dataWithOveralls = useMemo(() => {
        return formattedData.map((data) => {
            const activitySumObj = activityOveralls.find((activity) => activity.id === data.id)

            return {
                ...data,
                [overallLabel]: activitySumObj?.count,
            }
        })
    }, [formattedData, activityOveralls, overallLabel])

    const totalsObj = useMemo(() => {
        const activityTotals = getTotals(reports)
        const totalOverall = activityOveralls.reduce((acc, curr) => acc + curr.count, 0)

        return {
            id: 'Ukupno',
            name: totalLabel,
            ...activityTotals,
            [overallLabel]: totalOverall,
        }
    }, [reports, activityOveralls, totalLabel, overallLabel])

    const chartData = [totalsObj, ...dataWithOveralls]

    const numObjects = chartData?.length
    const numFields = numObjects && Object.keys(chartData?.[0]).length - 2 // exclude id, name
    const [totalHeight, barSize, barGap] = handleChartHeight(numObjects, numFields)

    const { overallColor, reportColors } = handleChartColors(reports.length)

    return (
        <div style={{ height: `${totalHeight}px` }} className="w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    layout="vertical"
                    data={chartData}
                    barSize={barSize}
                    barGap={barGap}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 100,
                        bottom: 50,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" tickMargin={10} />
                    <Tooltip contentStyle={{ fontSize: '11px' }} />
                    <Legend iconSize={16} />

                    <Bar key="overall" dataKey={overallLabel} fill={overallColor}>
                        <LabelList
                            dataKey={overallLabel}
                            position="right"
                            fill="#000"
                            fontSize={12}
                            formatter={(value: number) => (value > 0 ? value : '')}
                        />
                    </Bar>

                    {reports?.map((item, index) => (
                        <Bar key={item.agent?.id} dataKey={`${item.agent?.firstName} ${item.agent?.lastName}`} fill={reportColors[index]}>
                            <LabelList
                                dataKey={`${item.agent?.firstName} ${item.agent?.lastName}`}
                                position="right"
                                fill="#000"
                                fontSize={12}
                                formatter={(value: number) => (value > 0 ? value : '')}
                            />
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default AgentActivityReportChart
